import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { Vector3Tuple } from 'three';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
// import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
// import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js';
import { useBlendShape, useTextureLoadMonitor } from '~/hooks';
import {
  useAnimation,
  useExpression,
  useAvatarQueue,
  useRandomEyeBlink,
} from '~/hooks/Avatar';
import { AvatarInfo } from '~/types';
import useChatAvatarStore from '~/store/useChatAvatarStore';
import useChatPageLoadingStatus from '~/store/useChatPageLoadingStatusStore';
import useToast from '~/store/useToast';

export const VisemePrefix = 'viseme_';
export const Lerp_Speed = 0.1;
export const EyeBlinkKey = ['eyeBlinkLeft', 'eyeBlinkRight'];

const MAX_GLTF_LOAD_RETRY_COUNT = 1; // 아바타 로딩 최대 재시도 횟수
const GLTF_RELOAD_DELAY = 1000; // 에러 발생 이후 1초 후 아바타 로딩 재시도

interface CharacterProps {
  avatar: AvatarInfo;
  visible?: boolean;
  position: Vector3Tuple;
  scale?: Vector3Tuple;
}

export default function Character({
  avatar,
  visible,
  position,
  scale = [8, 8, 8], // Default scale is [1, 1, 1]
}: CharacterProps) {
  const { url: avatarUrl, id, idle_motion_path: idleMotion } = avatar;

  const characterRef = useRef<THREE.Mesh>(null!);
  const { showToast } = useToast();
  const { setChatPageLoadingEnd, isChatPageLoading } =
    useChatPageLoadingStatus();

  const [scene, setScene] = useState<THREE.Group>();
  const [animations, setInitialAnimations] = useState<THREE.AnimationClip[]>(
    [],
  );
  const [isAvatarLoadOnError, setIsAvatarLoadOnError] = useState(false);

  const loader = useMemo(() => new GLTFLoader(), []);
  // MeshoptDecoder 사용시
  loader.setMeshoptDecoder(MeshoptDecoder);

  // DracoLoader 사용시
  // const dracoLoader = new DRACOLoader();

  // dracoLoader.setDecoderConfig({ type: 'js' });
  // dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
  // loader.setDRACOLoader(dracoLoader);

  const { setInitialCharacterState, setMorph, resetMorph, setMorphKeys } =
    useChatAvatarStore((state) => ({
      setInitialCharacterState: state.setInitialCharacterState,
      setMorph: state.setMorphs,
      resetMorph: state.resetMorph,
      setMorphKeys: state.setMorphKeys,
    }));
  const { setAnimations } = useChatAvatarStore((state) => ({
    setAnimations: state.setAnimations,
  }));

  useTextureLoadMonitor({
    scene,
    onLoadError: () => setIsAvatarLoadOnError(true),
  });

  useEffect(() => {
    setInitialCharacterState({ id });
    resetMorph({ id });
    if (scene) {
      scene.traverse((x) => {
        if (
          x instanceof THREE.Mesh &&
          x.material instanceof THREE.MeshStandardMaterial
        ) {
          x.material.roughness = 0.7; // 거칠기 조정
          x.material.metalness = 0.05; // 금속성 조정
          x.material.envMapIntensity = 1; // HDR 조명 강화
        } else if (x instanceof THREE.Mesh && Array.isArray(x.material)) {
          // 다중 재질 메시 처리
          x.material.forEach((mat) => {
            if (mat instanceof THREE.MeshStandardMaterial) {
              mat.roughness = 0.7;
              mat.metalness = 0.05;
              mat.envMapIntensity = 1;
            }
          });
        }
      });
    }
  }, [scene, id]);

  useEffect(() => {
    let timeout;

    const loadModel = (
      type: 'IdleMotion' | 'AvatarModel',
      url: string,
      retryCount: number,
    ) => {
      if (!url) return;

      loader.load(
        url,
        (gltf) => {
          setScene(gltf.scene);
          setInitialAnimations(gltf.animations);

          // 아바타 로딩 완료 => 채팅 페이지 진입 로딩 레이아웃 off
          // 로딩 종료 이후 확실한 아바타 렌더 상태 보장을 위한 1초 추가 Waiting
          if (type === 'AvatarModel')
            isChatPageLoading &&
              setTimeout(() => setChatPageLoadingEnd(), 1000);
        },
        undefined,
        (error) => {
          // 아바타 or idle 모션 로딩 실패
          console.error(`${type} 로드 실패:`, error);

          if (
            type === 'AvatarModel' &&
            MAX_GLTF_LOAD_RETRY_COUNT > retryCount
          ) {
            timeout = setTimeout(() => {
              showToast(
                'error',
                `Failed to Load ${type}. Trying to load again.`,
              );
              loadModel(type, url, ++retryCount);
            }, GLTF_RELOAD_DELAY);
          } else {
            showToast('error', `Failed to Load ${type}.`);
            // 에러시 로딩화면 off
            isChatPageLoading && setTimeout(() => setChatPageLoadingEnd(), 500);
            type === 'AvatarModel' && setIsAvatarLoadOnError(true);
          }
        },
      );
    };

    loadModel('AvatarModel', avatarUrl, 0);
    idleMotion && loadModel('IdleMotion', idleMotion, 0);

    return timeout && clearTimeout(timeout);
  }, [avatarUrl, idleMotion]);

  useEffect(() => {
    if (isAvatarLoadOnError) throw new Error(`Avatar 파일 로드 실패`);
  }, [isAvatarLoadOnError]);

  useEffect(() => {
    if (animations.length > 0) {
      setAnimations({
        id,
        animations,
      });
    }
  }, [animations, id]);

  useBlendShape(scene, '/final_stable_smile.json', 24);
  // useRandomEyeBlink({ id });
  // useExpression({ id });
  useAnimation({ scene, id });
  useAvatarQueue({ id });

  return (
    <>
      {scene && (
        <>
          <primitive
            key={id}
            visible={visible}
            ref={characterRef}
            object={scene}
            position={position}
            scale={scale} // Apply the scale here
          />
        </>
      )}
    </>
  );
}
